import React, { DOMAttributes } from 'react';

import { classNames } from '@/helpers/classNames';
import { SvgIcon } from '@material-ui/core';

import { AsaphIconButton } from '../Button';
import { Loader } from '../Loader';
import styles from './SheetLayout.module.scss';

type SheetLayoutProps = {
	className?: string;
	topComponentClassName?: string;
	titleClassName?: string;
	actions?: {
		icon: React.ElementType;
		onClick: DOMAttributes<HTMLDivElement>['onClick'];
		hidden?: boolean;
		selected?: boolean;
		className?: string;
	}[];
	titleComponent?: React.ReactNode;
	topComponent?: React.ReactNode;
	noPadding?: boolean;
	isLoading?: boolean;
	fullHeight?: boolean;
	bottomComponent?: React.ReactNode;
	bottomPanelOpen?: boolean;
	bottomPanelContent?: React.ReactNode;
	onBottomPanelClose?: () => void;
};

export const SheetLayout: React.FC<SheetLayoutProps> = ({
	className,
	topComponentClassName,
	titleClassName,
	children,
	actions,
	topComponent,
	titleComponent,
	noPadding,
	isLoading,
	fullHeight,
	bottomComponent,
	bottomPanelOpen,
	bottomPanelContent,
	onBottomPanelClose,
}) => {
	const classes = classNames([styles['root'], className]);

	return (
		<div className={classes}>
			<div className={styles['top-content']}>
				<div className={styles['header']}>
					<div className={classNames([styles['title'], titleClassName])}>{titleComponent}</div>
					<div className={styles['actions']}>
						{actions?.map(
							(a, i) =>
								!a.hidden && (
									<div
										key={`sheet-layout-action-${i}`}
										className={classNames([
											styles['action'],
											a.selected && styles['selected'],
											a.className,
										])}
										onClick={a.onClick}
									>
										<SvgIcon component={a.icon} />
									</div>
								),
						)}
					</div>
				</div>
				{topComponent && (
					<div
						className={classNames([
							styles['top-component'],
							topComponentClassName,
						])}
					>
						{topComponent}
					</div>
				)}
			</div>
			<div
				className={classNames([
					styles['content'],
					!noPadding && styles['padding'],
					(isLoading || fullHeight) && styles['full-height'],
				])}
			>
				<div
					className={classNames([
						styles['bottom-sheet-overlay'],
						bottomPanelOpen && styles['active'],
					])}
					onClick={onBottomPanelClose}
				/>
				{isLoading ? <Loader /> : children}
			</div>
			{bottomComponent && (
				<div className={styles['bottom-component']}>
					{bottomComponent}
				</div>
			)}

			<div
				className={classNames([
					styles['bottom-panel'],
					bottomPanelOpen && styles['active'],
				])}
			>
				{bottomPanelContent}
			</div>
		</div>
	);
};
